import React, { useState, useMemo, useEffect } from 'react';
import { Survey, SurveyResponse, SurveyCategory } from '../types/survey';
import { fetchSectionsByAge, fetchSectionsForHealth, submitSurvey } from '../services/surveyService';
import { validateResponses, getAssessmentResultMessage, calculateRiskScore } from '../utils/surveyUtils';
import QuestionComponent from './QuestionComponent';
import StepIndicator from './StepIndicator';
import { ReportModal } from './ReportModal';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from './Logo';
import { RegularUser } from '../types/admin';
import './SurveyComponent.css';
import { Link, useNavigate } from 'react-router-dom';

const SurveyComponent: React.FC = () => {
	// Auth context
	const { user, logout } = useAuth();
	const navigate = useNavigate();

	// User type state
	const [userType, setUserType] = useState<'social' | 'health' | null>(user?.type === 'health' ? 'health' : 'social');

	const [age, setAge] = useState<number | null>(null);
	const [ageInputValue, setAgeInputValue] = useState<string>('');
	const [showCategorySelection, setShowCategorySelection] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<'kids' | 'adults' | 'health-workers' | null>(null);
	const [survey, setSurvey] = useState<Survey | null>(null);
	const [responses, setResponses] = useState<SurveyResponse[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [validationErrors, setValidationErrors] = useState<string[]>([]);
	const [isCompleted, setIsCompleted] = useState(false);
	const [assessmentResult, setAssessmentResult] = useState<ReturnType<typeof getAssessmentResultMessage> | null>(null);

	// Report modal state
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [reportMessage, setReportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
	const [submittedSurveyId, setSubmittedSurveyId] = useState<string | null>(null);
	const [riskScore, setRiskScore] = useState<number>(0);

	// Step navigation state
	const [currentStep, setCurrentStep] = useState(0);
	const [stepErrors, setStepErrors] = useState<{ [stepIndex: number]: string[] }>({});

	// Scroll to top when step changes
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [currentStep]);

	useEffect(() => {
		if (userType === 'health') {
			// Directly load health worker questions
			loadSurveyForHealthWorkers();
		}
	}, [userType]);

	const handleAgeSubmit = async (submittedAge: number) => {
		setAge(submittedAge);
		await loadSurveyByAge(submittedAge);
	};

	const handleCategorySelection = async (category: 'kids' | 'adults') => {
		setSelectedCategory(category);
		// Set age to null since we don't know the exact age
		setAge(null);
		// Load survey based on category (simulate age: kids < 18, adults >= 18)
		const simulatedAge = category === 'kids' ? 16 : 25;
		await loadSurveyByAge(simulatedAge);
	};

	const handleUnknownAge = () => {
		setShowCategorySelection(true);
	};

	const handleUserTypeSelection = async (type: 'social' | 'health') => {
		setUserType(type);
		if (type === 'health') {
			// Directly load health worker questions
			await loadSurveyForHealthWorkers();
		}
		// For social workers, show age selection
	};

	const loadSurveyForHealthWorkers = async () => {
		try {
			setIsLoading(true);
			setError(null);
			// Load questions specifically for health workers
			// Using a special age indicator for health workers
			const sections = await fetchSectionsForHealth(); // Special code for health workers
			// Create survey object with sections
			const surveyData: Survey = {
				id: 'health-worker-survey',
				title: 'Upitnik za zdravstvene radnike',
				description: 'Procena rizika od trgovine ljudima',
				targetGroup: 'health-workers' as any,
				sections: sections,
			};
			setSurvey(surveyData);

			// Initialize responses with null values for all questions
			const initialResponses: SurveyResponse[] = sections.flatMap((section) =>
				section.questions.map((question) => ({
					questionId: question.id,
					answer: null,
				}))
			);
			setResponses(initialResponses);
		} catch (err) {
			setError('Greška prilikom učitavanja upitnika. Molimo pokušajte ponovo.');
			console.error('Error loading health worker survey:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const loadSurveyByAge = async (userAge: number) => {
		try {
			setIsLoading(true);
			setError(null);
			const sections = await fetchSectionsByAge(userAge);
			// Create survey object with sections
			const surveyData: Survey = {
				id: 'current-survey',
				title: userAge < 18 ? 'Upitnik za decu' : 'Upitnik za odrasle',
				description: 'Procena rizika od trgovine ljudima',
				targetGroup: userAge < 18 ? 'kids' : 'adults',
				sections: sections,
			};
			setSurvey(surveyData);

			// Initialize responses with null values for all questions
			const initialResponses: SurveyResponse[] = sections.flatMap((section) =>
				section.questions.map((question) => ({
					questionId: question.id,
					answer: null,
				}))
			);
			setResponses(initialResponses);
		} catch (err) {
			setError('Greška prilikom učitavanja upitnika. Molimo pokušajte ponovo.');
			console.error('Error loading survey:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleResponseChange = (questionId: string, answer: string | boolean | number) => {
		setResponses((prev) => {
			const existingIndex = prev.findIndex((r) => r.questionId === questionId);
			if (existingIndex >= 0) {
				return prev.map((response, index) => (index === existingIndex ? { ...response, answer } : response));
			} else {
				return [...prev, { questionId, answer }];
			}
		});

		if (validationErrors.length > 0) {
			setValidationErrors([]);
		}
	};

	const getResponseValue = (questionId: string) => {
		const response = responses.find((r) => r.questionId === questionId);
		return response?.answer;
	};

	const handleSubmit = async () => {
		if (!survey) return;

		// If we're not on the last step, go to next step
		if (currentStep < groupedQuestions.length - 1) {
			goToNextStep();
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			// Calculate risk score
			const calculatedRiskScore = calculateRiskScore(survey, responses);
			setRiskScore(calculatedRiskScore);

			// Get assessment result message
			const result = getAssessmentResultMessage(survey, responses);
			setAssessmentResult(result);

			// Create submission object with new format - only include answered questions
			const submission = {
				age: userType === 'health' ? 0 : age!, // Health workers use 0 as placeholder
				targetGroup: survey.targetGroup,
				riskLevel: result.riskLevel,
				userType: userType,
				answers: responses
					.filter((r) => r.answer !== null) // Only include answered questions
					.map((r) => ({
						questionId: r.questionId,
						answer: r.answer,
					})),
			};

			// Submit survey to new API
			const userSurvey = await submitSurvey(submission);

			// Save survey ID for report submission
			setSubmittedSurveyId(userSurvey.id);

			console.log('Submitted survey:', userSurvey);

			// If potential trafficking detected, could trigger additional actions
			if (userSurvey.potentialTrafficking) {
				console.log('Potential trafficking detected by backend:', userSurvey);
			}

			setIsCompleted(true);
		} catch (err) {
			setError('Greška prilikom slanja upitnika. Molimo pokušajte ponovo.');
			console.error('Error submitting survey:', err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const goBack = () => {
		navigate('/dashboard');
	};

	const resetSurvey = () => {
		goBack();
		// if (user?.type == 'health') {
		// } else {
		// 	setUserType(null);
		// 	setAge(null);
		// 	setAgeInputValue('');
		// 	setShowCategorySelection(false);
		// 	setSelectedCategory(null);
		// 	setSurvey(null);
		// 	setResponses([]);
		// 	setIsCompleted(false);
		// 	setValidationErrors([]);
		// 	setAssessmentResult(null);
		// 	setError(null);
		// 	setCurrentStep(0);
		// 	setStepErrors({});
		// 	// Reset report modal state
		// 	setIsReportModalOpen(false);
		// 	setReportMessage(null);
		// 	setSubmittedSurveyId(null);
		// 	setRiskScore(0);
		// }
	};

	// Report modal functions
	const handleCreateReport = () => {
		console.log('submittedSurveyId:', submittedSurveyId);

		setIsReportModalOpen(true);
		setReportMessage(null);
	};

	const handleReportSubmitted = (success: boolean, message: string) => {
		setReportMessage({
			type: success ? 'success' : 'error',
			text: message,
		});

		if (success) {
			setIsReportModalOpen(false);
		}
	};

	// Step navigation functions
	const validateCurrentStep = (stepIndex: number) => {
		if (!survey || !groupedQuestions[stepIndex]) return { isValid: true, errors: [] };

		const currentGroup = groupedQuestions[stepIndex];
		const errors: string[] = [];

		currentGroup.questions.forEach((question) => {
			const response = responses.find((r) => r.questionId === question.id);
			if (!response || response.answer === null || response.answer === '') {
				errors.push(`Molimo odgovorite na pitanje: ${question.title}`);
			}
		});

		return { isValid: errors.length === 0, errors };
	};

	const goToNextStep = () => {
		// const validation = validateCurrentStep(currentStep);

		// if (!validation.isValid) {
		// 	setStepErrors((prev) => ({
		// 		...prev,
		// 		[currentStep]: validation.errors,
		// 	}));
		// 	return;
		// }

		// // Clear current step errors
		// setStepErrors((prev) => {
		// 	const newErrors = { ...prev };
		// 	delete newErrors[currentStep];
		// 	return newErrors;
		// });

		const currentGroup = groupedQuestions[currentStep];
		currentGroup.questions.forEach((question) => {
			const response = responses.find((r) => r.questionId === question.id);
			if (!response || response.answer === null || response.answer === '') {
				setResponses((prev) => {
					const questionId = question.id;
					const existingIndex = prev.findIndex((r) => r.questionId === questionId);
					const answer = false;
					if (existingIndex >= 0) {
						return prev.map((response, index) => (index === existingIndex ? { ...response, answer } : response));
					} else {
						return [...prev, { questionId: question.id, answer: false }];
					}
				});
			}
		});

		if (currentStep < groupedQuestions.length - 1) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const goToPreviousStep = () => {
		if (currentStep > 0) {
			setCurrentStep((prev) => prev - 1);
			// Clear any errors for the previous step
			setStepErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[currentStep - 1];
				return newErrors;
			});
		}
	};

	const goToStep = (stepIndex: number) => {
		// Only allow going to completed steps
		const canGoToStep = stepIndex <= currentStep || stepIndex === 0;
		if (canGoToStep && stepIndex >= 0 && stepIndex < groupedQuestions.length) {
			setCurrentStep(stepIndex);
		}
	};

	// Group questions by category/section - support both new and legacy structures
	const groupedQuestions = useMemo(() => {
		if (!survey) return [];

		// New sections structure
		if (survey.sections) {
			return survey.sections.map((section) => ({
				category: { id: section.id, name: section.title },
				questions: section.questions,
			}));
		}

		// Legacy categories structure
		if (survey.categories && survey.questions) {
			const categoryGroups: { [categoryId: string]: { category: SurveyCategory; questions: typeof survey.questions } } = {};

			survey.questions.forEach((question) => {
				const questionCategory = (question as any).category;
				if (!categoryGroups[questionCategory]) {
					const category = survey.categories!.find((cat) => cat.id === questionCategory);
					if (category) {
						categoryGroups[questionCategory] = {
							category,
							questions: [],
						};
					}
				}
				if (categoryGroups[questionCategory]) {
					categoryGroups[questionCategory].questions.push(question);
				}
			});

			return Object.values(categoryGroups);
		}

		return [];
	}, [survey]);

	// Create steps for StepIndicator
	const steps = useMemo(() => {
		return groupedQuestions.map((group, index) => {
			// Check if step is completed - all questions in this step have answers
			const stepQuestions = group.questions;
			const allAnswered = stepQuestions.every((q) => {
				const response = responses.find((r) => r.questionId === q.id);
				return response && response.answer !== null && response.answer !== '';
			});

			const isCompleted = index < currentStep || (index === currentStep && allAnswered);
			const isActive = index === currentStep;

			return {
				id: group.category.id,
				name: group.category.name,
				status: isCompleted ? ('completed' as const) : isActive ? ('active' as const) : ('pending' as const),
			};
		});
	}, [groupedQuestions, currentStep, responses]);

	// Get current step data
	const currentStepData = groupedQuestions[currentStep];

	// Helper function for total questions
	const getTotalQuestions = () => {
		if (!survey) return 0;
		if (survey.sections) {
			return survey.sections.reduce((total, section) => total + section.questions.length, 0);
		}
		return survey.questions?.length || 0;
	};

	// Reusable header component
	const Header = useMemo(() => {
		if (!user) return null;

		return (
			<header className='dashboard-header'>
				<div className='header-content'>
					<Link to='/' className='back-link' title='Idi na početnu stranicu'>
						<Logo size='small' />
					</Link>
					<div className='nav-user-info'>
						<h1>
							Dobrodošli, {(user as RegularUser).firstName} {(user as RegularUser).lastName}
						</h1>
						<p className='user-details'>
							{(user as RegularUser).institution} • {(user as RegularUser).email}
						</p>
					</div>
					<div className='header-actions'>
						<button className='btn btn-secondary' style={{ visibility: 'hidden', transition: 'none' }}>
							Izmeni podatke
						</button>
						<button className='btn btn-outline' onClick={logout}>
							Odjavi se
						</button>
					</div>
				</div>
			</header>
		);
	}, [user, logout]);

	// Age/Category selection screen
	if (!userType) {
		// First, show user type selection
		return (
			<div style={{ marginTop: -20 }}>
				{Header}
				<div className='survey-container'>
					<header className='survey-header'>
						<h1>Procena rizika od trgovine ljudima</h1>
						<p className='survey-description'>Molimo odaberite vašu profesiju:</p>
					</header>
					<div className='user-type-selection'>
						<div className='user-type-options'>
							<button onClick={() => handleUserTypeSelection('social')} className='category-option social-worker' disabled={isLoading}>
								<div className='user-type-icon'>👥</div>
								<div className='user-type-content'>
									<h3>Socijalni radnik</h3>
									<p>Za procenu rizika kod drugih osoba</p>
								</div>
							</button>

							<button onClick={() => handleUserTypeSelection('health')} className='category-option health-worker' disabled={isLoading}>
								<div className='user-type-icon'>🏥</div>
								<div className='user-type-content'>
									<h3>Zdravstveni radnik</h3>
									<p>Za procenu pacijenata</p>
								</div>
							</button>
						</div>

						{isLoading && <div className='loading-message'>Učitavanje upitnika...</div>}
						{error && <div className='error-message'>{error}</div>}
					</div>
				</div>
			</div>
		);
	}

	// Age/Category selection screen for social workers only
	if (userType === 'social' && !age && !selectedCategory) {
		// Show category selection if user chose "I don't know the age"
		if (showCategorySelection) {
			return (
				<div style={{ marginTop: -20 }}>
					{Header}

					<div className='survey-container'>
						{/* <h1>Odaberite kategoriju</h1>
						<p>Pošto ne znate tačan broj godina, molimo odaberite odgovarajuću kategoriju:</p> */}
						<header className='survey-header'>
							<button onClick={() => setShowCategorySelection(false)} className='btn btn-back' title='Vrati se na početak'>
								←
							</button>
							<h1>Odaberite kategorij</h1>
							<p className='survey-description'>Pošto ne znate tačan broj godina, molimo odaberite odgovarajuću kategoriju:</p>
						</header>
						<div className='category-selection'>
							<div className='category-options'>
								<button onClick={() => handleCategorySelection('kids')} className='category-option kids' disabled={isLoading}>
									<div className='category-icon'>👶</div>
									<div className='category-content'>
										<h3>Deca</h3>
										<p>Za osobe mlađe od 18 godina</p>
									</div>
								</button>

								<button onClick={() => handleCategorySelection('adults')} className='category-option adults' disabled={isLoading}>
									<div className='category-icon'>👤</div>
									<div className='category-content'>
										<h3>Odrasli</h3>
										<p>Za osobe starije od 18 godina</p>
									</div>
								</button>
							</div>

							{isLoading && <div className='loading-message'>Učitavanje upitnika...</div>}
							{error && <div className='error-message'>{error}</div>}
						</div>
					</div>
				</div>
			);
		}

		return (
			<div style={{ marginTop: -20 }}>
				{Header}
				<div className='survey-container'>
					<header className='survey-header'>
						<button onClick={goBack} className='btn btn-back' title='Vrati se na početak'>
							←
						</button>
						<h1>Procena rizika od trgovine ljudima</h1>
						<p className='survey-description'>Molimo unesite godine osobe za koju vršite procenu ili odaberite kategoriju ako ne znate tačan broj:</p>
					</header>
					<div className='age-selection'>
						<div className='age-options'>
							<div className='age-input-section'>
								<label htmlFor='age-input' className='age-label'>
									Unesite godine:
								</label>
								<div className='age-input-wrapper'>
									<input
										id='age-input'
										type='number'
										min='0'
										max='120'
										value={ageInputValue}
										onChange={(e) => setAgeInputValue(e.target.value)}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												const ageNum = parseInt(ageInputValue);
												if (ageNum > 0 && ageNum <= 120) {
													handleAgeSubmit(ageNum);
												} else {
													setError('Molimo unesite validne godine (1-120).');
												}
											}
										}}
										placeholder='npr. 25'
										className='age-input'
										autoFocus
									/>
									<button
										onClick={async () => {
											const ageNum = parseInt(ageInputValue);
											if (ageNum > 0 && ageNum <= 120) {
												await handleAgeSubmit(ageNum);
											} else {
												setError('Molimo unesite validne godine (1-120).');
											}
										}}
										disabled={!ageInputValue || isLoading}
										className='btn btn-primary age-submit-btn'
									>
										{isLoading ? 'Učitavanje...' : 'Nastavi'}
									</button>
								</div>
							</div>

							<div className='age-divider'>
								<span>ili</span>
							</div>

							<div className='unknown-age-section'>
								<button onClick={handleUnknownAge} className='btn btn-outline unknown-age-btn' disabled={isLoading}>
									Ne znam tačan broj godina
								</button>
								<p className='unknown-age-description'>Odabraćete kategoriju (deca/odrasli)</p>
							</div>
						</div>

						{error && <div className='error-message'>{error}</div>}
					</div>
				</div>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className='survey-container'>
				{/* User header */}
				{user && (
					<header className='dashboard-header'>
						<div className='header-content'>
							<Logo size='small' />
							<div className='nav-user-info'>
								<h1>
									Dobrodošli, {(user as RegularUser).firstName} {(user as RegularUser).lastName}
								</h1>
								<p className='user-details'>
									{(user as RegularUser).institution} • {(user as RegularUser).email}
								</p>
							</div>
							<div className='header-actions'>
								<button className='btn btn-outline' onClick={logout}>
									Odjavi se
								</button>
							</div>
						</div>
					</header>
				)}
				<div className='loading'>
					<p>Učitavanje upitnika...</p>
				</div>
			</div>
		);
	}

	if (error && !survey) {
		return (
			<div className='survey-container'>
				{/* User header */}
				{user && (
					<header className='dashboard-header'>
						<div className='header-content'>
							<Logo size='small' />
							<div className='nav-user-info'>
								<h1>
									Dobrodošli, {(user as RegularUser).firstName} {(user as RegularUser).lastName}
								</h1>
								<p className='user-details'>
									{(user as RegularUser).institution} • {(user as RegularUser).email}
								</p>
							</div>
							<div className='header-actions'>
								<button className='btn btn-outline' onClick={logout}>
									Odjavi se
								</button>
							</div>
						</div>
					</header>
				)}
				<div className='error'>
					<p>{error}</p>
					<button onClick={resetSurvey} className='btn btn-secondary'>
						Počni iznova
					</button>
				</div>
			</div>
		);
	}

	if (!survey) {
		return (
			<div className='survey-container'>
				{/* User header */}
				{user && (
					<header className='dashboard-header'>
						<div className='header-content'>
							<Logo size='small' />
							<div className='nav-user-info'>
								<h1>
									Dobrodošli, {(user as RegularUser).firstName} {(user as RegularUser).lastName}
								</h1>
								<p className='user-details'>
									{(user as RegularUser).institution} • {(user as RegularUser).email}
								</p>
							</div>
							<div className='header-actions'>
								<button className='btn btn-outline' onClick={logout}>
									Odjavi se
								</button>
							</div>
						</div>
					</header>
				)}
				<div className='error'>
					<p>Upitnik nije dostupan.</p>
				</div>
			</div>
		);
	}

	if (isCompleted) {
		return (
			<div style={{ marginTop: -20 }}>
				{Header}

				<div className='survey-container'>
					<div className='completion-message'>
						<div className={`assessment-result ${assessmentResult?.type || ''}`}>
							<div className='result-header'>
								<div className='result-icon'>
									{assessmentResult?.type === 'high-risk' && '⚠️'}
									{assessmentResult?.type === 'medium-risk' && '🔍'}
									{assessmentResult?.type === 'low-risk' && '✅'}
								</div>
								<h2>{assessmentResult?.title || 'Procena završena'}</h2>
							</div>

							<div className='result-content'>
								<div className='result-message'>
									{assessmentResult?.message.split('\n\n').map((paragraph, index) => (
										<p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
									))}
								</div>

								{assessmentResult?.contactInfo && (
									<div className='contact-info'>
										<h3>Kontakt informacije</h3>
										<div className='contact-details'>
											<div className='contact-item'>
												<strong>Telefon:</strong>
												<a href={`tel:${assessmentResult.contactInfo.phone}`}>{assessmentResult.contactInfo.phone}</a>
											</div>
											<div className='contact-item'>
												<strong>Email:</strong>
												<a href={`mailto:${assessmentResult.contactInfo.email}`}>{assessmentResult.contactInfo.email}</a>
											</div>
											{assessmentResult.contactInfo.emergency && (
												<div className='contact-item emergency'>
													<strong>Hitni slučaj (Policija):</strong>
													<a href={`tel:${assessmentResult.contactInfo.emergency}`}>{assessmentResult.contactInfo.emergency}</a>
												</div>
											)}
										</div>
									</div>
								)}

								{/* Report message */}
								{reportMessage && (
									<div
										className={`report-message ${reportMessage.type}`}
										style={{
											padding: '12px',
											margin: '12px 0',
											borderRadius: '4px',
											backgroundColor: reportMessage.type === 'success' ? '#d4edda' : '#f8d7da',
											color: reportMessage.type === 'success' ? '#155724' : '#721c24',
											border: `1px solid ${reportMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
										}}
									>
										{reportMessage.text}
									</div>
								)}
							</div>
							{(assessmentResult as any)?.showReportButton && submittedSurveyId && (
								<div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
									<button
										onClick={handleCreateReport}
										className='btn btn-primary'
										style={{
											backgroundColor: '#28a745',
											marginRight: '12px',
										}}
									>
										📋 Kreiraj prijavu
									</button>
								</div>
							)}
						</div>

						<div className='action-buttons'>
							<button onClick={resetSurvey} className='btn btn-secondary'>
								Nazad na početak
							</button>
						</div>
					</div>
					{/* Report Modal */}
					{submittedSurveyId && <ReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} surveyId={submittedSurveyId} riskScore={riskScore} onReportSubmitted={handleReportSubmitted} />}
				</div>
			</div>
		);
	}

	return (
		<div style={{ marginTop: -20, paddingBottom: 20 }}>
			{Header}
			<div className='survey-container'>
				<header className='survey-header'>
					<button onClick={resetSurvey} className='btn btn-back' title='Vrati se na početak'>
						←
					</button>
					<h1>{survey.description}</h1>
					<p className='survey-description'>{survey.title}</p>

					{/* Step Indicator */}
					<StepIndicator steps={steps} currentStep={currentStep} onStepClick={goToStep} />
				</header>

				<div className='survey-content'>
					{currentStepData && (
						<div className='current-step'>
							{!!currentStepData.category.name && (
								<div className='step-header'>
									<h2 className='step-title'>{currentStepData.category.name}</h2>
									{(currentStepData.category as any).description && <p className='step-description'>{(currentStepData.category as any).description}</p>}
									{groupedQuestions.length > 1 && (
										<div className='step-progress'>
											<span className='step-counter'>
												Korak {currentStep + 1} od {groupedQuestions.length}
											</span>
										</div>
									)}
								</div>
							)}

							<form className='survey-form'>
								<div className='step-questions'>
									{currentStepData.questions.map((question, questionIndex) => (
										<div key={question.id} className='question-wrapper' style={{ animationDelay: `${questionIndex * 0.1}s` }}>
											<div className='question-number'>
												Pitanje {questionIndex + 1} od {currentStepData.questions.length}
											</div>
											<QuestionComponent question={question} value={getResponseValue(question.id)} onChange={(value) => handleResponseChange(question.id, value)} />
										</div>
									))}
								</div>

								{/* Current step validation errors */}
								{stepErrors[currentStep] && stepErrors[currentStep].length > 0 && (
									<div className='validation-errors'>
										<h4>Molimo popunite sledeće:</h4>
										<ul>
											{stepErrors[currentStep].map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ul>
									</div>
								)}

								{/* Global validation errors (for final step) */}
								{validationErrors.length > 0 && currentStep === groupedQuestions.length - 1 && (
									<div className='validation-errors'>
										<h4>Molimo popunite sledeće:</h4>
										<ul>
											{validationErrors.map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ul>
									</div>
								)}

								{error && <div className='error-message'>{error}</div>}

								<div className='step-navigation'>
									{
										<button type='button' onClick={goToPreviousStep} disabled={currentStep === 0} className='btn btn-secondary'>
											← Prethodno
										</button>
									}

									<div className='step-info'>
										{/* <span>
										{responses.filter((r) => r.answer !== null).length} od {getTotalQuestions()} pitanja
									</span> */}
									</div>

									<button type='button' onClick={handleSubmit} disabled={isSubmitting} className='btn btn-primary'>
										{isSubmitting ? 'Šalje se...' : currentStep === groupedQuestions.length - 1 ? 'Završi upitnik' : 'Sledeće →'}
									</button>
								</div>
							</form>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SurveyComponent;
